// src/services/FirebaseService.js
import {
  auth,
  createUserWithEmailAndPassword,
  facebookAuthProvider,
  googleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  deleteUser,
  sendPasswordResetEmail,
  confirmPasswordReset,
  verifyPasswordResetCode,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from 'configs/firebaseAuth';

const FirebaseService = {};

FirebaseService.getCurrentUser = () => auth.currentUser;

FirebaseService.reauthenticateUser = async (email, password) => {
  const user = auth.currentUser;
  if (!user) throw new Error('auth/user-not-found');
  const credential = EmailAuthProvider.credential(email, password);
  await reauthenticateWithCredential(user, credential);
  return { success: true };
};

FirebaseService.updateUserPassword = async newPassword => {
  const user = auth.currentUser;
  if (!user) throw new Error('auth/user-not-found');
  await updatePassword(user, newPassword);
  return { success: true };
};

FirebaseService.deleteUserFromAuth = async () => {
  try {
    await deleteUser(auth.currentUser);
  } catch (err) {
    throw new Error(err.code);
  }
};

FirebaseService.signInEmailRequest = async ({ email, password }) => {
  try {
    return await signInWithEmailAndPassword(auth, email, password);
  } catch (err) {
    console.error('[Firebase signIn] error code:', err?.code, '| message:', err?.message);
    return err;
  }
};

FirebaseService.signOutRequest = async () => {
  try {
    return await signOut(auth);
  } catch (err) {
    return err;
  }
};

FirebaseService.signInGoogleRequest = async () => {
  try {
    return await signInWithPopup(auth, googleAuthProvider);
  } catch (err) {
    return err;
  }
};

FirebaseService.signInFacebookRequest = async () => {
  try {
    return await signInWithPopup(auth, facebookAuthProvider);
  } catch (err) {
    return err;
  }
};

FirebaseService.signUpEmailRequest = async (email, password) => {
  try {
    return await createUserWithEmailAndPassword(auth, email, password);
  } catch (err) {
    throw new Error(err.code);
  }
};

FirebaseService.sendPasswordResetEmailRequest = async email => {
  try {
    return await sendPasswordResetEmail(auth, email);
  } catch (err) {
    throw new Error(err.code);
  }
};

FirebaseService.confirmPasswordResetRequest = async (oobCode, newPassword) => {
  try {
    return await confirmPasswordReset(auth, oobCode, newPassword);
  } catch (err) {
    throw new Error(err.code);
  }
};

FirebaseService.verifyPasswordResetCodeRequest = async oobCode => {
  try {
    return await verifyPasswordResetCode(auth, oobCode);
  } catch (err) {
    throw new Error(err.code);
  }
};

export default FirebaseService;
